import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Card, Col, Empty, Form, Input, Pagination, Row, Select, Space, Spin, Statistic, Table, Tag } from 'antd';
import { Download, Database, FileJson, Table2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './AdminTelemetry.css';

const AdminTelemetry = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'org_admin' || user?.role === 'platform_admin';

  const [modules, setModules] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [moduleId, setModuleId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [message, setMessage] = useState('');
  const [offset, setOffset] = useState(0);
  const [summary, setSummary] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const limit = 50;

  useEffect(() => {
    if (!isAdmin) return;
    const loadModules = async () => {
      try {
        const response = await axios.get('/api/admin/modules', {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        setModules(response.data || []);
      } catch (error) {
        setMessage('Failed to load modules.');
      }
    };
    loadModules();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const loadCatalog = async () => {
      try {
        const response = await axios.get('/api/admin/telemetry/event-catalog', {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        setCatalog(response.data.events || []);
      } catch (error) {
        setCatalog([]);
      }
    };
    loadCatalog();
  }, [isAdmin]);

  const fetchSummary = async () => {
    try {
      const response = await axios.get('/api/admin/telemetry/summary', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        params: {
          module_id: moduleId || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined
        }
      });
      setSummary(response.data);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to load telemetry summary.');
    }
  };

  const fetchSessions = async (nextOffset = 0) => {
    setLoading(true);
    setMessage('');
    try {
      const response = await axios.get('/api/admin/telemetry/sessions', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        params: {
          module_id: moduleId || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          limit,
          offset: nextOffset
        }
      });
      setSessions(response.data.sessions || []);
      setTotal(response.data.total || 0);
      setOffset(nextOffset);
      if (nextOffset === 0) {
        await fetchSummary();
      }
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to load telemetry sessions.');
    } finally {
      setLoading(false);
    }
  };

  const downloadStandard = async (format) => {
    try {
      const response = await axios.get('/api/admin/telemetry/exports/standard', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        params: {
          module_id: moduleId || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          format
        },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const scope = moduleId || 'all-modules';
      link.href = url;
      link.setAttribute('download', `${scope}-telemetry-standard.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setMessage(`Failed to export ${format.toUpperCase()} telemetry.`);
    }
  };

  const downloadSession = async (session) => {
    try {
      const response = await axios.get(`/api/admin/telemetry/sessions/${session.session_id}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        params: { module_id: session.module_id },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${session.module_id}-${session.session_id}.jsonl.zst`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setMessage('Failed to download session file.');
    }
  };

  const downloadAll = async () => {
    if (!moduleId) {
      setMessage('Select a module to export.');
      return;
    }
    try {
      const response = await axios.get('/api/admin/telemetry/exports', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        params: {
          module_id: moduleId,
          start_date: startDate || undefined,
          end_date: endDate || undefined
        },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${moduleId}-telemetry.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setMessage('Failed to export telemetry files.');
    }
  };

  if (!isAdmin) {
    return (
      <div className="telemetry-page">
        <Card className="telemetry-card">
          <h2>Admin Access Required</h2>
          <p>Please sign in with an administrator account.</p>
        </Card>
      </div>
    );
  }

  const sessionColumns = [
    {
      title: 'Module',
      dataIndex: 'module_id',
      render: (value) => <Tag color="processing">{value}</Tag>,
    },
    {
      title: 'Session',
      dataIndex: 'session_id',
      render: (value) => <span className="telemetry-session">{value}</span>,
    },
    {
      title: 'Events',
      dataIndex: 'event_count',
    },
    {
      title: 'Text Inputs',
      dataIndex: 'text_input_count',
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      render: (value) => (value ? new Date(value).toLocaleString() : '-'),
    },
    {
      title: 'Ended',
      dataIndex: 'ended_at',
      render: (value) => (value ? new Date(value).toLocaleString() : '-'),
    },
    {
      title: 'File',
      key: 'file',
      render: (_, session) => (
        <Button type="link" onClick={() => downloadSession(session)} disabled={!session.file_exists}>
          Download
        </Button>
      ),
    },
  ];

  const catalogColumns = [
    {
      title: 'Event Type',
      dataIndex: 'event_type',
      key: 'eventType',
      render: (value) => <strong>{value}</strong>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Privacy',
      dataIndex: 'privacy',
      key: 'privacy',
      render: (value) => <em>{value}</em>,
    },
  ];

  return (
    <div className="telemetry-page">
      <div className="telemetry-header">
        <div>
          <h1><Database size={20} /> Telemetry Data</h1>
          <p>Export per-game session files for model training.</p>
        </div>
        <Button type="primary" icon={<Download size={16} />} onClick={downloadAll}>
          Export All
        </Button>
      </div>

      <Card className="telemetry-filters">
        <Form className="telemetry-filter-form" layout="inline" onFinish={() => fetchSessions(0)}>
          <Form.Item label="Module">
            <Select
              value={moduleId}
              onChange={setModuleId}
              options={[
                { value: '', label: 'All Modules' },
                ...modules.map((mod) => ({ value: mod.module_id, label: mod.title })),
              ]}
            />
          </Form.Item>
          <Form.Item label="Start Date">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Form.Item>
          <Form.Item label="End Date">
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Form.Item>
          <Form.Item>
            <Button htmlType="submit" loading={loading}>
              Search
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {message && <Alert className="telemetry-message" type="error" showIcon message={message} />}

      {summary && (
        <Row className="telemetry-summary" gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} xl={5}><Card><Statistic title="Total Events" value={summary.total_events} /></Card></Col>
          <Col xs={24} sm={12} xl={5}><Card><Statistic title="Sessions" value={summary.total_sessions} /></Card></Col>
          <Col xs={24} sm={12} xl={5}><Card><Statistic title="Modules" value={summary.total_modules} /></Card></Col>
          <Col xs={24} sm={12} xl={5}><Card><Statistic title="Text Inputs" value={summary.text_input_events} /></Card></Col>
          <Col xs={24} xl={4}>
          <Space className="telemetry-actions" wrap>
            <Button icon={<FileJson size={16} />} onClick={() => downloadStandard('json')}>
              xAPI JSON
            </Button>
            <Button icon={<Table2 size={16} />} onClick={() => downloadStandard('csv')}>
              CSV
            </Button>
          </Space>
          </Col>
        </Row>
      )}

      {summary && (
        <div className="telemetry-breakdown">
          <Card className="telemetry-panel">
            <h3>Event Types</h3>
            {(summary.event_types || []).slice(0, 8).map((item) => (
              <div className="telemetry-stat-row" key={item.event_type}>
                <span>{item.event_type}</span>
                <strong>{item.count}</strong>
              </div>
            ))}
          </Card>
          <Card className="telemetry-panel">
            <h3>Modules</h3>
            {(summary.modules || []).slice(0, 8).map((item) => (
              <div className="telemetry-stat-row" key={item.module_id}>
                <span>{item.module_id}</span>
                <strong>{item.events} events / {item.sessions} sessions</strong>
              </div>
            ))}
          </Card>
        </div>
      )}

      {!!catalog.length && (
        <Card className="telemetry-catalog" title="Collected Data Plan">
          <Table
            rowKey="event_type"
            columns={catalogColumns}
            dataSource={catalog}
            pagination={false}
            scroll={{ x: 720 }}
          />
        </Card>
      )}

      <Table
        className="telemetry-table"
        rowKey="session_id"
        columns={sessionColumns}
        dataSource={sessions}
        loading={loading ? { indicator: <Spin /> } : false}
        pagination={false}
        scroll={{ x: 980 }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No sessions found." /> }}
      />

      <div className="telemetry-pagination">
        <Pagination
          current={Math.floor(offset / limit) + 1}
          pageSize={limit}
          total={total}
          showSizeChanger={false}
          showTotal={(count, range) => `${range[0]}-${range[1]} of ${count}`}
          disabled={loading}
          onChange={(page) => fetchSessions((page - 1) * limit)}
        />
      </div>
    </div>
  );
};

export default AdminTelemetry;
